import { CommonModule } from '@angular/common';
import { Component, signal } from '@angular/core';
import { RouterModule, RouterOutlet } from '@angular/router';
import { HasPermissionDirective } from '@core/directives/has-permission.directive';
import { CATEGORY_SETTINGS, PERMISSIONS, ROUTES } from '@core/enum/common.enum';
import {
  ISettingsItem,
  ISettingsChildren,
} from '@core/interface/common.inteface';
import { PermissionService } from '@core/services/presenters/permission.service';
import { SidebarService } from '@core/services/presenters/sidebar.service';
import { LayoutSidebarItemGroupComponent } from '@shared/design/organisms/layout-sidebar/layout-sidebar-item-group.component';
import { LayoutSidebarItemComponent } from '@shared/design/organisms/layout-sidebar/layout-sidebar-item.component';
import { LayoutSidebarLogoComponent } from '@shared/design/organisms/layout-sidebar/layout-sidebar-logo.component';
import { LayoutSidebarComponent } from '@shared/design/organisms/layout-sidebar/layout-sidebar.component';
import { LayoutTopbarComponent } from '@shared/design/organisms/layout-topbar/layout-topbar.component';
import { SETTINGS_GROUPS } from '@shared/utils/constants/common.constant';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterModule,
    CommonModule,
    LayoutSidebarComponent,
    LayoutSidebarLogoComponent,
    LayoutSidebarItemComponent,
    LayoutSidebarItemGroupComponent,
    LayoutTopbarComponent,
    HasPermissionDirective,
  ],
})
export class LayoutComponent {
  isScrolled = signal(false);
  readonly PERMISSIONS = PERMISSIONS;
  readonly ROUTES = ROUTES;
  readonly CATEGORY_SETTINGS = CATEGORY_SETTINGS;
  readonly sidebarItems = SETTINGS_GROUPS[CATEGORY_SETTINGS.SIDEBAR];

  constructor(
    readonly sidebarService: SidebarService,
    private permissionService: PermissionService,
  ) {}

  hasAnyToolsPermissions(): boolean {
    const toolsPermissions = [
      PERMISSIONS.PURCHASE_ORDER_LIST,
      PERMISSIONS.PURCHASE_TRANSACTION_LIST,
      PERMISSIONS.RECONCILIATION_TRANSACTION_LIST,
      PERMISSIONS.RECONCILIATION_DASHBOARD_SUMMARY,
      PERMISSIONS.MANAGEMENT_SCHEDULE_LIST,
    ];

    return this.permissionService.hasAnyPermissionSync(toolsPermissions);
  }

  hasAnyConfigurationPermissions(): boolean {
    const configPermissions = [
      PERMISSIONS.USER_PERSON_LIST,
      PERMISSIONS.ORGANIZATION_COMPANY_LIST,
      PERMISSIONS.CAPABILITY_ROLE_LIST,
      PERMISSIONS.ACTIVITY_EVENT_LIST,
      PERMISSIONS.MANAGEMENT_WEBHOOK_LIST,
      PERMISSIONS.ORGANIZATION_STORE_LIST,
      PERMISSIONS.PURCHASE_CHANNEL_LIST,
      PERMISSIONS.EXCHANGE_RATE_LIST,
    ];

    return this.permissionService.hasAnyPermissionSync(configPermissions);
  }

  onMainScroll(event: Event): void {
    const target = event.target as HTMLElement;
    this.isScrolled.set(target.scrollTop > 50);
  }

  getItemPermissions(item: ISettingsItem): string[] {
    const parentPermission = item.permission ? [item.permission] : [];
    const childrenPermissions =
      item.childrens && item.childrens.length > 0
        ? item.childrens
            .map((child: ISettingsChildren) => child.permission)
            .filter((permission): permission is PERMISSIONS => !!permission)
        : [];

    return [...parentPermission, ...childrenPermissions];
  }
}
